// Raw input state + edge events. Attack/weapon semantics live in weapons.js
// and controller.js; this layer reports keys, clicks and chain timing.
import { uiOwnsKey } from './keyfocus.mjs';

const WEAPON_KEYS = {
  Digit1: 0, Digit2: 1, Digit3: 2, Digit4: 3, Digit5: 4, Digit6: 5, Digit7: 6,
};

// ---------------------------------------------------------------------------
// Keyboard ownership. The join screen — the FIRST screen every player sees —
// used to share the keyboard with the game: typing a name drove WASD, death,
// reload, interact, stance, roll and weapon swap behind the dialog, and Space
// could not be typed at all because the jump bind preventDefault()s it.
//
// Two gates, both checked before anything else happens in keydown:
//   1. a modal UI layer (setKeyboardModal) — the dialog owns the keyboard for
//      its whole lifetime, even before the player has clicked the name field;
//   2. a focused text-entry element (keyfocus.mjs) — covers everything after
//      the dialog, e.g. a future settings panel or a chat box.
// KEYUP IS NEVER GATED: a key pressed in game and released while a field has
// focus must still leave this.keys, or the character runs into the sea.
let modalDepth = 0;
let active = null; // the one live Input on the page (set in the constructor)

// UI layers that own the keyboard call this on show (true) and hide (false).
// It is a counter, not a flag, so two layers cannot un-modal each other.
export function setKeyboardModal(on) {
  modalDepth = on ? modalDepth + 1 : Math.max(0, modalDepth - 1);
  // Entering a modal with W held would leave the character sprinting behind
  // the dialog for as long as it is open — the keyup is going to be swallowed
  // by the field, so drop the held state here instead.
  if (modalDepth > 0 && active) {
    active.keys.clear();
    active.lmb = false;
    active.rmb = false;
  }
}

export function keyboardModalDepth() { return modalDepth; }

// The OTHER window-level keydown handlers have to ask the same question, and
// they cannot see `modalDepth` from here otherwise. src/ui/mapoverlay.js is
// the one that matters: it binds KeyM and preventDefault()s it, which is why
// the letter "m" cannot be typed into the join screen's name field even with
// the gate below in place, and why the map toggles behind the dialog. The
// debug panels (F3, F6, F10) are function keys and merely rude.
//   window.addEventListener('keydown', (e) => {
//     if (!keyEventIsForGame(e)) return;
//     ...
export function keyEventIsForGame(e) {
  return !uiOwnsKey({
    target: e && e.target,
    activeElement: typeof document !== 'undefined' ? document.activeElement : null,
    modalDepth,
  });
}

export class Input {
  constructor(canvas) {
    this.keys = new Set();
    this.pointerLocked = false;
    this.mouseDX = 0;
    this.mouseDY = 0;
    this.rmb = false;
    this.lmb = false;
    this.listeners = {
      attack: [], attackRelease: [], jump: [], toggleCrouch: [], toggleProne: [],
      roll: [], die: [], help: [], weapon: [], weaponCycle: [], reload: [], gunMelee: [],
      animdbg: [], animlog: [], inventory: [], interact: [], quickslot: [],
    };
    this.lastClickAt = 0;
    this.chain = 0;       // consecutive clicks (< 450 ms apart)
    this.lastKeyTap = {}; // key -> time, for double-tap detection

    canvas.addEventListener('click', () => {
      // A modal layer keeps the cursor: grabbing pointer lock here would hide
      // the pointer over the dialog the player is still filling in.
      if (modalDepth > 0) return;
      if (!this.pointerLocked) canvas.requestPointerLock();
    });
    document.addEventListener('pointerlockchange', () => {
      this.pointerLocked = document.pointerLockElement === canvas;
    });
    document.addEventListener('mousemove', (e) => {
      if (!this.pointerLocked) return;
      this.mouseDX += e.movementX;
      this.mouseDY += e.movementY;
    });
    document.addEventListener('mousedown', (e) => {
      if (!this.pointerLocked) return;
      if (e.button === 0) {
        this.lmb = true;
        const now = performance.now();
        this.chain = now - this.lastClickAt < 450 ? this.chain + 1 : 0;
        this.lastClickAt = now;
        this.emit('attack', { chain: this.chain, dirs: this.dirs() });
      } else if (e.button === 2) {
        this.rmb = true;
      }
    });
    document.addEventListener('mouseup', (e) => {
      if (e.button === 0) { this.lmb = false; this.emit('attackRelease'); }
      if (e.button === 2) this.rmb = false;
    });
    document.addEventListener('contextmenu', (e) => e.preventDefault());
    document.addEventListener('wheel', (e) => {
      if (this.pointerLocked) this.emit('weaponCycle', { dir: e.deltaY > 0 ? 1 : -1 });
    }, { passive: true });

    document.addEventListener('keydown', (e) => {
      // THE GATE. Before this.keys, before preventDefault, before any emit —
      // so a keystroke that belongs to a text field leaves no trace at all in
      // the game: no held key, no swallowed Space, no stance change.
      if (uiOwnsKey({ target: e.target, activeElement: document.activeElement, modalDepth })) return;
      if (e.repeat) return;
      const k = e.code;
      this.keys.add(k);
      const now = performance.now();
      const doubleTap = now - (this.lastKeyTap[k] || 0) < 280;
      this.lastKeyTap[k] = now;

      if (k === 'Space') { e.preventDefault(); this.emit('jump'); }
      if (k === 'KeyC') this.emit('toggleCrouch');
      if (k === 'KeyX') this.emit('toggleProne');
      if (k === 'KeyV') this.emit('roll', { dir: this.strafeDir() });
      if (k === 'KeyK') this.emit('die');
      if (k === 'KeyR') this.emit('reload');
      // F = pick up / interact (falls through to gun-melee when nothing is in
      // reach — see main.js). G stays a pure interact for muscle memory.
      if (k === 'KeyF') this.emit('interact', { fromF: true });
      if (k === 'KeyH') this.emit('weapon', { index: 0 }); // holster -> fists
      if (k in WEAPON_KEYS) this.emit('weapon', { index: WEAPON_KEYS[k] });
      if (k === 'F1') { e.preventDefault(); this.emit('help'); }
      if (k === 'F2') { e.preventDefault(); this.emit('animdbg'); }
      if (k === 'F4') { e.preventDefault(); this.emit('animlog'); }
      if (k === 'Tab') { e.preventDefault(); this.emit('inventory'); }
      if (k === 'KeyG') this.emit('interact');
      // quickslots sit AFTER the weapon keys on the number row: 8 9 0 -
      if (k === 'Digit8') this.emit('quickslot', { slot: 0 });
      if (k === 'Digit9') this.emit('quickslot', { slot: 1 });
      if (k === 'Digit0') this.emit('quickslot', { slot: 2 });
      // `Minus` is the PHYSICAL key right of 0 on a US layout; on a Danish (or
      // German, or French) keyboard that key is '+', and the '-' the quickbar
      // prints sits somewhere else entirely. e.key carries the character the
      // layout actually produces, so accept either — the label stops lying
      // without giving up the US position or the numpad.
      if (k === 'Minus' || e.key === '-') this.emit('quickslot', { slot: 3 });
      // Ctrl = sneak; stop the browser reacting where it lets us (note:
      // Chrome still owns Ctrl+W — Alt works as a safe alias)
      if (k === 'ControlLeft') e.preventDefault();
      // double-tap A/D rolls sideways (prone-roll when prone, dodge otherwise)
      if (doubleTap && (k === 'KeyA' || k === 'KeyD')) {
        this.emit('roll', { dir: k === 'KeyA' ? -1 : 1 });
      }
    });
    // NOT gated on focus: releasing is always safe, and swallowing a keyup is
    // how a key gets stuck down.
    document.addEventListener('keyup', (e) => this.keys.delete(e.code));
    window.addEventListener('blur', () => { this.keys.clear(); this.lmb = false; this.rmb = false; });
    active = this;
    window.__input = this; // test/video-pipeline hook (headless has no pointer lock)
  }

  emit(name, arg) { for (const fn of this.listeners[name]) fn(arg); }
  on(name, fn) { this.listeners[name].push(fn); }

  // Consumed once per frame by the camera.
  takeMouseDelta() {
    const d = { x: this.mouseDX, y: this.mouseDY };
    this.mouseDX = 0; this.mouseDY = 0;
    return d;
  }

  dirs() {
    return {
      w: this.keys.has('KeyW'), a: this.keys.has('KeyA'),
      s: this.keys.has('KeyS'), d: this.keys.has('KeyD'),
      sprint: this.keys.has('ShiftLeft') || this.keys.has('ShiftRight'),
    };
  }

  strafeDir() { return this.keys.has('KeyA') ? -1 : this.keys.has('KeyD') ? 1 : 0; }

  // Movement vector in camera-relative space: x = strafe, z = forward.
  moveAxis() {
    const d = this.dirs();
    const x = (d.d ? 1 : 0) - (d.a ? 1 : 0);
    const z = (d.w ? 1 : 0) - (d.s ? 1 : 0);
    return { x, z };
  }

  get sprint() { return this.keys.has('ShiftLeft') || this.keys.has('ShiftRight'); }
  // Ctrl = slow AND silent (sneak). NOTE: Chrome owns Ctrl+W (closes the tab)
  // and pages cannot block it — swap the bind if it bites in practice.
  get walkSlow() { return this.keys.has('ControlLeft') || this.keys.has('ControlRight'); }
  get sneak() { return this.keys.has('ControlLeft') || this.keys.has('ControlRight'); }
  // Alt = free-look: movement direction locks, the mouse looks around.
  get freelook() { return this.keys.has('AltLeft') || this.keys.has('AltRight'); }
  get leanL() { return this.keys.has('KeyQ'); }
  get leanR() { return this.keys.has('KeyE'); }
  get block() { return this.rmb; }
}
